import React, { useEffect, useState } from "react";
import { ArrowLeft, CalendarPlus, FileDown, Mail } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminTopBar from "./AdminTopBar";
import { openProjectClientMail } from "../lib/projectClientMail";
import { downloadProjectSnapshotPdf } from "../lib/projectSnapshotPdf";
import { supabase } from "../supabaseClient";

const projectStages = [
  { id: "event_preparation", label: "Event Preparation" },
  { id: "event_live", label: "Posted on Live Event" },
];

const stageIndexById = projectStages.reduce((accumulator, stage, index) => {
  accumulator[stage.id] = index;
  return accumulator;
}, {});

const normalizeStage = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/\s+/g, "_");
  return stageIndexById[normalized] !== undefined ? normalized : "event_preparation";
};

const stageLabelById = projectStages.reduce((accumulator, stage) => {
  accumulator[stage.id] = stage.label;
  return accumulator;
}, {});

const statusLabel = (value) => {
  const normalized = String(value || "approved").trim().toLowerCase().replace(/\s+/g, "_");
  if (normalized === "in_review") return "In Review";
  if (normalized === "initial_consultation") return "Initial Consultation";
  if (normalized === "proposal_contract" || normalized === "proposal_quotation" || normalized === "agreement_confirmation" || normalized === "contract_signing") {
    return "Proposal & Contract";
  }
  if (normalized === "event_preparation" || normalized === "accepted" || normalized === "approved") return "Event Preparation";
  if (normalized === "event_live") return "Posted on Live Event";
  if (normalized === "declined") return "Declined";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/_/g, " ");
};

const resolveProjectStage = (row) => {
  const candidates = [row?.project_stage, row?.booking_status];
  for (const candidate of candidates) {
    const normalized = String(candidate || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (stageIndexById[normalized] !== undefined) {
      return normalized;
    }
  }
  return "event_preparation";
};

const formatDisplayDate = (value) => {
  if (!value) return "TBD";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "TBD";
  return parsed.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" });
};

const readonlyInputClass = "mt-1 h-9 w-full rounded border border-gray-300 bg-gray-50 px-3 text-xs text-gray-800";
const readonlyTextAreaClass = "w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-800";

export default function ProjectDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", id)
        .in("status", ["accepted", "approved", "event_preparation", "event_live"])
        .single();

      if (error) {
        console.error("Failed to load project details:", error);
        setProject(null);
        setLoading(false);
        return;
      }

      const requestCode = String(data?.request_code || "").trim();
      let hasPublishedLiveEvent = false;

      if (requestCode) {
        const { data: linkedEventRows, error: linkedEventError } = await supabase
          .from("events")
          .select("id")
          .eq("booking_request_code", requestCode)
          .in("status", ["published", "upcoming"])
          .limit(1);

        if (linkedEventError) {
          console.error("Failed to check linked live event:", linkedEventError);
        } else {
          hasPublishedLiveEvent = Boolean(linkedEventRows?.length);
        }
      }

      setProject({
        ...data,
        project_stage: hasPublishedLiveEvent ? "event_live" : normalizeStage(resolveProjectStage(data)),
      });
      setLoading(false);
    };

    fetchProject();

    const channel = supabase
      .channel(`admin-project-details:${id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "bookings", filter: `id=eq.${id}` }, fetchProject)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, fetchProject)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [id]);

  const currentStageLabel = stageLabelById[project?.project_stage] || "Event Preparation";
  const canEmailClient = Boolean(String(project?.requester_email || "").trim());

  const downloadProjectSnapshot = () => {
    if (!project) return;
    downloadProjectSnapshotPdf(project);
  };

  const openMailClient = () => {
    openProjectClientMail(project);
  };

  const vendorChoices = project?.concert_vendor_choices || {};
  const isConcert = String(project?.booking_type || "").toLowerCase() !== "corporate";
  const signedContractUrl = project?.signed_contract_url || "";
  const signedContractPath = project?.signed_contract_path || "";

  return (
    <div className="min-h-screen bg-[#f3f3f3]">
      <AdminTopBar />

      <div className="px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8">
        <div className="w-full max-w-7xl mx-auto min-w-0">
          <button
            type="button"
            onClick={() => navigate("/admin/projects")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black mb-4"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </button>

          {loading ? (
            <div className="rounded-xl border border-gray-300 bg-white p-8 text-sm text-gray-500">Loading project details...</div>
          ) : !project ? (
            <div className="rounded-xl border border-gray-300 bg-white p-8 text-sm text-gray-500">Project not found.</div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5 min-w-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">{project.request_code || `Project #${project.id}`}</p>
                  <h1 className="mt-1 text-[28px] md:text-[32px] font-black text-gray-900 tracking-tight break-words">{project.event_name || "Untitled Event"}</h1>
                  <p className="mt-1 text-sm text-gray-600 break-words">{project.requester_name || "Unknown"} • {project.requester_email || "No email"}</p>
                  <p className="mt-1 text-sm text-gray-500 break-words">{project.booking_type || "booking"} • {project.venue || "No venue"} • {formatDisplayDate(project.event_date || project.created_at)}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                    {statusLabel(project.status)}
                  </span>
                  <span className="inline-flex rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
                    {currentStageLabel}
                  </span>
                </div>
              </div>

              <div className="mt-4 space-y-4">
                <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                  <div className="bg-black px-4 py-2 text-sm font-bold text-white">Client and Event Information</div>
                  <div className="grid gap-6 p-4 md:grid-cols-[1.2fr_0.8fr] md:p-6">
                    <div className="grid grid-cols-1 gap-3">
                      <label className="text-xs font-semibold text-gray-700">
                        Client Name
                        <input value={project.requester_name || ""} readOnly className={readonlyInputClass} />
                      </label>

                      <label className="text-xs font-semibold text-gray-700">
                        Contact Information
                        <input value={project.requester_email || ""} readOnly className={readonlyInputClass} />
                      </label>

                      <label className="text-xs font-semibold text-gray-700">
                        Email
                        <input value={project.requester_email || ""} readOnly className={readonlyInputClass} />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:pt-1">
                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Theme
                        <input value={project.theme || ""} readOnly className={readonlyInputClass} />
                      </label>

                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Venue
                        <input value={project.venue || ""} readOnly className={readonlyInputClass} />
                      </label>

                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Budget
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <input value={project.budget_range || ""} readOnly className={readonlyInputClass} />
                          <input value={project.attendee_count || ""} readOnly className={readonlyInputClass} />
                        </div>
                      </label>

                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Event Name
                        <input value={project.event_name || ""} readOnly className={readonlyInputClass} />
                      </label>
                    </div>
                  </div>
                </div>

                {isConcert ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="divide-y divide-gray-400 text-sm text-gray-800">
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Technical Rider :</span>
                        <input value={vendorChoices.technical_rider || "Let EchoJham Provide"} readOnly className="h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Musical Director :</span>
                        <input value={vendorChoices.musical_director || "Let EchoJham Provide"} readOnly className="h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Food:</span>
                        <input value={vendorChoices.food || "Let EchoJham Provide"} readOnly className="h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs" />
                      </div>
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Additional Requirements:</p>
                        <textarea value={project.details || ""} readOnly rows={5} className={readonlyTextAreaClass} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="divide-y divide-gray-400 text-sm text-gray-800">
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Specific Requirements</p>
                        <textarea value={project.specific_needs || ""} readOnly rows={4} className={readonlyTextAreaClass} />
                      </div>
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Event Details & Other Requirements:</p>
                        <textarea value={project.details || ""} readOnly rows={5} className={readonlyTextAreaClass} />
                      </div>
                    </div>
                  </div>
                )}

                {Array.isArray(project.attachments) && project.attachments.length ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="bg-black px-4 py-2 text-sm font-bold text-white">Attachments</div>
                    <div className="px-4 py-4 md:px-6 text-sm text-gray-800 space-y-2">
                      {project.attachments.map((item, index) => (
                        <div key={`${item?.path || item?.name || index}`} className="break-all">
                          {item?.url ? (
                            <a href={item.url} target="_blank" rel="noreferrer" className="text-blue-700 underline">
                              {item.name || item.path || `Attachment ${index + 1}`}
                            </a>
                          ) : (
                            <span>{item?.name || item?.path || `Attachment ${index + 1}`}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {signedContractUrl || signedContractPath ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="bg-black px-4 py-2 text-sm font-bold text-white">Signed Contract</div>
                    <div className="px-4 py-4 md:px-6 text-sm text-gray-800">
                      {signedContractUrl ? (
                        <a href={signedContractUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">
                          {signedContractPath || signedContractUrl}
                        </a>
                      ) : (
                        <p className="break-all">{signedContractPath || "Signed contract uploaded"}</p>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 pt-1">
                  <p className="text-xs text-gray-500">Updated {formatDisplayDate(project.updated_at || project.created_at)}</p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() =>
                        navigate("/admin/create-event", { state: { fromBooking: project } })
                      }
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-gray-700"
                    >
                      <CalendarPlus size={14} /> Create Live Event
                    </button>
                    <button
                      type="button"
                      onClick={downloadProjectSnapshot}
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-[#8b0000] px-4 py-2 text-xs font-semibold text-white hover:bg-[#6f0000]"
                    >
                      <FileDown size={14} /> Export File
                    </button>
                    <button
                      type="button"
                      onClick={openMailClient}
                      disabled={!canEmailClient}
                      title={canEmailClient ? "Email this client in Gmail" : "No client email on this project"}
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg border border-gray-400 bg-white px-4 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Mail size={14} /> Email Client
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}